import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Info } from 'lucide-react'
import { useLocation, useHistory } from 'react-router-dom'

import {
  AdasBox,
  AdasAlert,
  AdasFormControl,
  AdasInputLabel,
  AdasSelect,
  AdasGrid,
  AdasPaper,
  AdasTypography
} from '@components/wrapper-components'
import { getReferralList } from 'api/api'
import { useCenterStore } from '@caradasstore/CenterStore'
import { Logger } from '../../../logger'

const PROFILE_OPTIONS = [
  {
    value: 'calibration',
    title: 'Store',
    subtitle: "Your store's standard pricing",
    info: 'Location standard pricing for walk-in customers and direct jobs. Editable by store managers.'
  },
  {
    value: 'auto_quote',
    title: 'Auto Quote',
    subtitle: 'Pricing applied when AI auto-quotes an incoming job',
    info: 'Pricing applied when AI auto-quotes an incoming job.'
  },
  {
    value: 'national',
    title: 'National Agreement',
    subtitle: 'Read-only pricing set by Car ADAS network partners',
    info: 'Read-only pricing set by Car ADAS network partners.'
  }
]

export const ProductProfileSelector = ({ onCategorySelect, selectedProfileType, shouldShowProductProfile }) => {
  const history = useHistory()
  const useQuery = () => {
    return new URLSearchParams(useLocation().search)
  }
  const query = useQuery()
  const profileFromQuery = query.get('profile')

  const [selectedProfile, setSelectedProfile] = useState(profileFromQuery || 'calibration')
  const [referralId, setReferralId] = useState('')
  const [referralSourceList, setReferralSourceList] = useState([])

  const ccId = useCenterStore((store) => store.ccId)
  const hasFetchedReferralSource = useRef(false)

  const fetchReferralSource = async () => {
    try {
      const referralListResponse = await getReferralList(ccId)
      if (referralListResponse?.status === 200) {
        const formattedReferralList = referralListResponse.data
          .sort((a, b) => a.company.localeCompare(b.company))
          .map((referralObj) => ({ id: referralObj.id, value: referralObj.company }))
        setReferralSourceList(formattedReferralList)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductProfileSelector/fetchReferralSource' } })
    }
  }

  useEffect(() => {
    if (selectedProfile === 'national' && !hasFetchedReferralSource.current) {
      fetchReferralSource()
      hasFetchedReferralSource.current = true
    }
  }, [selectedProfile])

  useEffect(() => {
    const effectiveProfile = selectedProfileType || profileFromQuery || 'calibration'
    setSelectedProfile(effectiveProfile)
    setReferralId('')
    hasFetchedReferralSource.current = false

    onCategorySelect?.({ referralId: '', profile: effectiveProfile })
  }, [selectedProfileType, profileFromQuery])

  const handleProfileChange = (profile) => {
    if (profile === 'national' && !shouldShowProductProfile) {
      // still allow selection so contact-support alert can show; preserve existing behaviour
    }
    setSelectedProfile(profile)
    setReferralId('')

    const params = new URLSearchParams()
    params.set('profile', profile)
    history.replace({ search: params.toString() })

    onCategorySelect?.({ referralId: '', profile })
  }

  const handleReferralChange = (e) => {
    const selectedId = e.target.value
    setReferralId(selectedId)
    onCategorySelect?.({ referralId: selectedId, profile: selectedProfile })
  }

  const activeOption = PROFILE_OPTIONS.find((opt) => opt.value === selectedProfile) || PROFILE_OPTIONS[0]

  return (
    <AdasBox>
      {/* Desktop-only profile picker — mobile has its own pill row in ProductsPage */}
      <div className='hidden min-[435px]:block'>
        <div className='mb-2 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>SELECT PROFILE</div>

        <div className='flex flex-col sm:flex-row gap-3'>
          {PROFILE_OPTIONS.map((option) => {
            const isSelected = selectedProfile === option.value
            const isNational = option.value === 'national'
            const isDisabled = isNational && !shouldShowProductProfile
            return (
              <button
                key={option.value}
                type='button'
                onClick={() => handleProfileChange(option.value)}
                disabled={isDisabled}
                className={`flex-1 flex items-start gap-3 p-4 rounded-lg text-left transition-colors border ${
                  isSelected ? 'bg-[#E8EFFF] border-[#1F6FEB]' : 'bg-white border-[#D1D5D8] hover:bg-[#F8FAFC]'
                } ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    isSelected ? 'border-[#0A1628]' : 'border-[#9CA3AF]'
                  }`}
                  aria-hidden='true'
                >
                  {isSelected && <span className='h-2.5 w-2.5 rounded-full bg-[#0A1628]' />}
                </span>
                <span className='flex flex-col'>
                  <span className='text-[14px] font-semibold text-[#0A1628]'>{option.title}</span>
                  <span className='text-[12px] text-[#6B7280] mt-0.5'>{option.subtitle}</span>
                </span>
              </button>
            )
          })}
        </div>

        <div className='mt-3 flex items-start gap-2 text-[12px] text-[#6B7280]'>
          <Info className='h-3.5 w-3.5 mt-0.5 shrink-0 text-[#6B7280]' />
          <span>{activeOption.info}</span>
        </div>
      </div>

      {selectedProfile === 'national' && !shouldShowProductProfile && (
        <AdasAlert severity='warning' className='rounded my-4 border border-[#FFE58F] text-[#614700] bg-[#FFFBE6]'>
          Please contact support to enroll in National Agreements
        </AdasAlert>
      )}

      {selectedProfile === 'national' && shouldShowProductProfile && (
        <AdasPaper elevation={0} className='p-4 rounded-md bg-[#f5faff] mt-4'>
          <AdasTypography variant='subtitle2' fontWeight='bold' gutterBottom mb={2}>
            DETAILS
          </AdasTypography>

          <AdasGrid container spacing={2}>
            <AdasGrid item xs={12} sm={8}>
              <AdasFormControl fullWidth className='mb-4 sm:mb-6 ml-0 sm:ml-4'>
                <AdasInputLabel id='insurance-label'>Select Referral Source</AdasInputLabel>
                <AdasSelect
                  labelId='insurance-label'
                  id='referral-select'
                  name='referral_id'
                  value={referralId}
                  onChange={handleReferralChange}
                  label='Select Referral Source'
                  options={referralSourceList}
                  className='rounded-lg'
                />
              </AdasFormControl>
            </AdasGrid>
          </AdasGrid>
        </AdasPaper>
      )}
    </AdasBox>
  )
}

ProductProfileSelector.propTypes = {
  onCategorySelect: PropTypes.func,
  selectedProfileType: PropTypes.string,
  shouldShowProductProfile: PropTypes.bool
}
